/* t26-lift.js - mouse tilt and the lift scroll story for sections/t26-hero.
   Writes CSS variables on the section; assets/t26-lift.css does the rest.

   THE LIFT FILM (18 Sep). 121 frames cut from the Higgsfield video, WebP at
   85% quality. Two sets, each in two packs (A: every 4th frame, loads first;
   B: the rest):
     desktop  1920x1080, the full frame
     phone    848x1080, the middle strip (x 536-1384 of the full frame), used
              when the screen only shows that strip (phones held upright)
   Pack format: "HHF1", uint16 frames in pack, uint16 total frames, then
   per frame uint16 index + uint32 byte length, then the WebP files in order.
   Film landmarks (frame): lift still 0-18, camera moves 18-40, light in the
   door gap 38, doors open 48-78, through the doorway 78-102, settles 102-120.
   The last frame matches the end photo.

   Scroll timeline (p = 0 at the top of the track, 1 at the bottom).
   Retimed 28 Sep (Jeremy): the track is 260vh (was 320vh) and words are on
   screen the whole way: the end-frame copy fades in while the card fades out,
   over the walk through the doorway, instead of after the film ends.
     0.00-0.02  nothing moves, the banner can be read
     0.02-0.30  the lift rises (film frames 0-36); floors on the display
     0.30-0.34  film runs on to frame 40
     0.34-0.50  doors open (frames 40-78)
     0.36-0.44  the card fades
     0.42-0.50  copy panel and award badge fade in (overlaps the card fade)
     0.48-0.56  the Shop (with price) and How it works buttons appear
     0.50-0.62  through the doorway (frames 78-102)
     0.62-0.68  the camera settles (frames 102-120)
     0.64-0.68  the film fades into the real photo
     0.68-0.94  markers, one after another (was 0.68-0.80; stretched 29 Sep,
                Jeremy, so each card gets more scroll), then a short hold
   SKIP LINK (28 Sep): "Skip to shop" jumps to the end of the track.
   Phones: the film is cropped to the middle of the frame. The phone track is
   longer (540vh, see t26-lift.css): the first 200vh plays the timeline
   above, and the extra 240vh (q, 0 to 1) is only for the side-to-side view
   of the end frame, so it moves slowly (Jeremy, 18 Sep):
     q 0.00-0.30  from the middle to the grandmother
     q 0.36-0.94  across to the pug, then a short hold
   Each marker shows while it is near the middle of the screen.

   PLAY BUTTON (19 Sep): the opening card's only action. It scrolls through
   the whole track at an even pace; any wheel, touch, key or click stops it. */
(function () {
  'use strict';

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function seg(p, a, b) { return clamp((p - a) / (b - a), 0, 1); }
  function ease(t) { return t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  /* Piecewise-linear lookup in [[x, y], ...]. */
  function table(pts, x) {
    if (x <= pts[0][0]) return pts[0][1];
    for (var i = 1; i < pts.length; i++) {
      if (x <= pts[i][0]) return lerp(pts[i - 1][1], pts[i][1], (x - pts[i - 1][0]) / (pts[i][0] - pts[i - 1][0]));
    }
    return pts[pts.length - 1][1];
  }

  var FLOORS = ['1', '2', '3', '4'];
  var BLINK = 0.2;          /* share of each floor spent fading in, and again out */
  var FW = 1920, FH = 1080; /* full film frame size */
  var STRIP_X = 536, STRIP_W = 848; /* the phone set's slice of the frame */
  var RISE = [0.02, 0.30];
  /* Share of the phone track that plays the timeline; the rest is the pan. */
  var PHONE_SPLIT = 200 / 440;
  /* Scroll position -> film frame. */
  var FILM = [[0, 0], [0.02, 0], [0.30, 36], [0.34, 40], [0.50, 78], [0.62, 102], [0.68, 120], [1, 120]];
  /* Film frame -> bottom edge of the lightbox (full-frame pixels), measured.
     Only used by the optional floor display. */
  var LIGHTBOX = [[0, 169], [18, 165], [24, 152], [30, 129], [36, 101], [40, 68], [44, 63], [48, 27]];

  function unpack(buf) {
    var dv = new DataView(buf);
    if (dv.getUint32(0) !== 0x48484631) return [];
    var n = dv.getUint16(4), o = 8 + n * 6, out = [];
    for (var k = 0; k < n; k++) {
      var idx = dv.getUint16(8 + k * 6), len = dv.getUint32(10 + k * 6);
      out.push({ i: idx, blob: new Blob([new Uint8Array(buf, o, len)], { type: 'image/webp' }) });
      o += len;
    }
    return out;
  }

  function init(hero) {
    if (!hero || hero.__hh) return;
    hero.__hh = true;

    var mqFine = window.matchMedia('(hover: hover) and (pointer: fine)');
    var mqCalm = window.matchMedia('(prefers-reduced-motion: reduce)');
    var mqNarrow = window.matchMedia('(max-width: 899px)');
    var style = hero.style;

    /* ---------------------------------------------------------- tilt */
    if (hero.getAttribute('data-tilt') === '1') {
      var tx = 0, ty = 0, cx = 0, cy = 0, traf = 0;
      var tstep = function () {
        cx += (tx - cx) * 0.08;
        cy += (ty - cy) * 0.08;
        style.setProperty('--hh-tx', cx.toFixed(4));
        style.setProperty('--hh-ty', cy.toFixed(4));
        traf = (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) ? requestAnimationFrame(tstep) : 0;
      };
      var tkick = function () { if (!traf) traf = requestAnimationFrame(tstep); };
      hero.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse' || !mqFine.matches || mqCalm.matches) return;
        var r = hero.getBoundingClientRect();
        tx = clamp(((e.clientX - r.left) / r.width - 0.5) * 2, -1, 1);
        ty = clamp(((e.clientY - r.top) / r.height - 0.5) * 2, -1, 1);
        tkick();
      });
      hero.addEventListener('pointerleave', function () { tx = 0; ty = 0; tkick(); });
    }

    /* -------------------------------------------------- scroll story */
    if (!hero.classList.contains('hh-hero--scroll')) return;
    /* Scripted story is live: CSS shows the skip link only with this class. */
    hero.classList.add('hh-js');
    var track = hero.parentElement;
    var seqEl = hero.querySelector('.hh-seq');
    var canvas = hero.querySelector('.hh-seq__canvas');
    var ctx = canvas ? canvas.getContext('2d') : null;
    var screen = hero.querySelector('[data-hh-floor]');
    var img = hero.querySelector('.hh-end__img');
    var spots = Array.prototype.slice.call(hero.querySelectorAll('.hh-spot'));
    var endLinks = Array.prototype.slice.call(hero.querySelectorAll('[data-hh-endlink]'));
    var baseX = parseFloat(hero.getAttribute('data-end-x') || '50') / 100;
    var last = -1, lastQ = -1, raf = 0, lastLabel = '', W = 0, H = 0, DPR = 1;

    /* Film frames by index, filled in as packs arrive. Two sets: the full
       frame (desktop) and the middle strip (phones). */
    var sets = {
      full: { frames: [], started: false, x: 0, w: FW, a: 'data-frames-a', b: 'data-frames-b' },
      strip: { frames: [], started: false, x: STRIP_X, w: STRIP_W, a: 'data-frames-ma', b: 'data-frames-mb' }
    };
    var shown = -1, shownSet = null, shownF = -1, want = 0;
    /* The strip is enough when the screen shows no more than its width. */
    function pickSet() {
      var s = Math.max(W / FW, H / FH);
      var visible = W / s;
      var strip = sets.strip;
      return (visible <= STRIP_W - 8 && seqEl && seqEl.getAttribute(strip.a)) ? strip : sets.full;
    }
    function nearest(list, i) {
      if (list[i]) return i;
      for (var d = 1; d < 121; d++) {
        if (list[i - d]) return i - d;
        if (list[i + d]) return i + d;
      }
      return -1;
    }
    /* The loaded frames either side of position x (a fraction, e.g. 37.4). */
    function bracket(list, x) {
      var a = Math.floor(x), b = Math.ceil(x);
      while (a >= 0 && !list[a]) a--;
      while (b <= 120 && !list[b]) b++;
      if (a < 0 && b > 120) return null;
      if (a < 0) return [b, b, 0];
      if (b > 120 || a === b) return [a, a, 0];
      return [a, b, (x - a) / (b - a)];
    }
    /* SMOOTH PLAYBACK (22 Sep). The film has 121 frames, so a scroll or the
       Play button moves through only 12-15 of them a second. Instead of
       jumping from frame to frame, the two frames either side of the exact
       position are cross-faded, which reads as smooth motion at the
       screen's full refresh rate. If pack B is still loading, it blends
       across the gap between pack A frames instead of stalling. */
    function draw(force) {
      if (!ctx) return;
      var set = pickSet();
      if (!set.started) start(set);
      var br = bracket(set.frames, want);
      if (!br && set !== sets.full) { set = sets.full; br = bracket(set.frames, want); }
      if (!br) return;
      var list = set.frames, f = br[2];
      /* Blend steps of 1/32 are invisible; skipping smaller changes saves work. */
      f = Math.round(f * 32) / 32;
      if (f >= 1) { br[0] = br[1]; f = 0; }
      if (!force && br[0] === shown && f === shownF && set === shownSet) return;
      var s = Math.max(W / FW, H / FH);
      var ox = (W - FW * s) / 2, oy = (H - FH * s) / 2;
      var dx = (ox + set.x * s) * DPR, dy = oy * DPR, dw = set.w * s * DPR, dh = FH * s * DPR;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.globalAlpha = 1;
      ctx.drawImage(list[br[0]], dx, dy, dw, dh);
      if (f > 0) {
        ctx.globalAlpha = f;
        ctx.drawImage(list[br[1]], dx, dy, dw, dh);
        ctx.globalAlpha = 1;
      }
      shown = br[0];
      shownF = f;
      shownSet = set;
    }
    function load(set, url) {
      if (!url) return Promise.resolve();
      return fetch(url).then(function (r) { return r.ok ? r.arrayBuffer() : null; }).then(function (buf) {
        if (!buf) return;
        return Promise.all(unpack(buf).map(function (fr) {
          /* Ready on load; decode() is only a head start, since a hidden
             tab can hold it back. */
          return new Promise(function (done) {
            var im = new Image();
            im.onload = function () {
              set.frames[fr.i] = im;
              if (im.decode) im.decode().catch(function () {});
              done();
            };
            im.onerror = function () { done(); };
            im.src = URL.createObjectURL(fr.blob);
          });
        }));
      }).then(function () { draw(true); }, function () {});
    }
    function start(set) {
      if (set.started || !seqEl || mqCalm.matches) return;
      set.started = true;
      load(set, seqEl.getAttribute(set.a)).then(function () {
        hero.classList.add('is-film');
        return load(set, seqEl.getAttribute(set.b));
      });
    }

    function measure() {
      W = hero.clientWidth;
      H = hero.clientHeight;
      /* Never draw the canvas sharper than the film itself (1920x1080): extra
         pixels cost speed and add no detail. */
      var src = 1 / Math.max(W / FW, H / FH, 0.0001);
      DPR = Math.max(1, Math.min(window.devicePixelRatio || 1, 2, src));
      if (canvas) {
        canvas.width = Math.round(W * DPR);
        canvas.height = Math.round(H * DPR);
        shown = -1;
        shownSet = null;
      }
    }
    measure();
    draw(true);

    /* Centre the card layer (glass, phones, tracker) on the film. Measured
       with the tilt and the fade reset, so it holds at any scroll point. */
    var inner = hero.querySelector('.hh-hero__inner');
    function centre() {
      if (!inner) return;
      var keep = [style.getPropertyValue('--hh-tx'), style.getPropertyValue('--hh-ty'), style.getPropertyValue('--hh-card')];
      style.setProperty('--hh-tx', '0');
      style.setProperty('--hh-ty', '0');
      style.setProperty('--hh-card', '1');
      style.setProperty('--hh-cx', '0px');
      style.setProperty('--hh-cy', '0px');
      var hr = hero.getBoundingClientRect();
      var l = Infinity, t = Infinity, r = -Infinity, b = -Infinity;
      Array.prototype.forEach.call(inner.querySelectorAll('.hh-glass, .hh-glass__tracker, .hh-stack__slot, .hh-tracker'), function (el) {
        var q = el.getBoundingClientRect();
        if (!q.width || !q.height) return;
        l = Math.min(l, q.left); t = Math.min(t, q.top);
        r = Math.max(r, q.right); b = Math.max(b, q.bottom);
      });
      style.setProperty('--hh-tx', keep[0]);
      style.setProperty('--hh-ty', keep[1]);
      style.setProperty('--hh-card', keep[2]);
      if (l === Infinity) return;
      var dx = hr.left + W / 2 - (l + r) / 2;
      var dy = hr.top + H / 2 - (t + b) / 2;
      /* Never push the top of the panel closer than 12px to the edge. */
      dy = Math.max(dy, hr.top + 12 - t);
      style.setProperty('--hh-cx', dx.toFixed(1) + 'px');
      style.setProperty('--hh-cy', dy.toFixed(1) + 'px');
    }
    centre();
    /* Again once fonts, images and the entrance animations have settled. */
    var centreT = 0;
    function centreSoon() { clearTimeout(centreT); centreT = setTimeout(centre, 120); }
    window.addEventListener('load', centreSoon);
    if (inner) {
      inner.addEventListener('transitionend', centreSoon);
      inner.addEventListener('animationend', centreSoon);
    }
    setTimeout(centre, 1600);
    setTimeout(centre, 3500);

    /* Dot offsets and the line back to each tracker. Phones always put the
       label under the dot, so the dot drops below the tracker there (at most
       80px, so the label clears the Shop button). */
    function offsets() {
      var narrow = mqNarrow.matches;
      spots.forEach(function (el) {
        var dx = parseFloat(el.getAttribute('data-dx')) || 0;
        var dy = parseFloat(el.getAttribute('data-dy')) || 0;
        if (narrow) dy = dy >= 0 ? Math.min(dy, 80) : 60;
        el.__dx = dx;
        el.style.setProperty('--dx', dx + 'px');
        el.style.setProperty('--dy', dy + 'px');
        el.style.setProperty('--len', Math.sqrt(dx * dx + dy * dy).toFixed(1) + 'px');
        el.style.setProperty('--ang', (Math.atan2(dy, dx) * 180 / Math.PI).toFixed(1) + 'deg');
      });
    }
    offsets();

    function place(panX) {
      if (!img || !img.naturalWidth) return;
      var sw = W, sh = H;
      var iw = img.naturalWidth, ih = img.naturalHeight;
      var s = Math.max(sw / iw, sh / ih);
      var dw = iw * s, dh = ih * s;
      var ox = (sw - dw) * panX, oy = (sh - dh) * 0.5;
      spots.forEach(function (el) {
        var x = ox + dw * parseFloat(el.getAttribute('data-x')) / 100;
        var y = oy + dh * parseFloat(el.getAttribute('data-y')) / 100;
        el.style.left = x.toFixed(1) + 'px';
        el.style.top = y.toFixed(1) + 'px';
        el.__x = x / sw;
        /* Phones: keep the centred label at least 10px inside the screen. */
        var lab = el.__lab || (el.__lab = el.querySelector('.hh-spot__label'));
        if (lab && mqNarrow.matches) {
          var half = lab.offsetWidth / 2;
          var c = x + (el.__dx || 0);
          var goal = clamp(c, 10 + half, sw - 10 - half);
          el.style.setProperty('--lx', (goal - c).toFixed(1) + 'px');
        } else if (lab) {
          el.style.removeProperty('--lx');
        }
      });
      /* ROW LAYOUT (29 Sep, Jeremy): on desktop the four labels sit in one
         row along the floor, evenly spaced from edge to edge, each tied back
         to its tracker by its line. The offsets are worked out here for the
         current window, so the row stays even at any size. Phones keep the
         offsets from the section. */
      if (!mqNarrow.matches && spots.length) {
        var labH = 0, labW = 0;
        spots.forEach(function (el) {
          var lab = el.__lab || (el.__lab = el.querySelector('.hh-spot__label'));
          if (lab) { labH = Math.max(labH, lab.offsetHeight); labW = Math.max(labW, lab.offsetWidth); }
        });
        var hr = hero.getBoundingClientRect();
        var floor = hero.querySelector('.hh-floorlogos');
        var fr = floor ? floor.getBoundingClientRect() : null;
        var bottom = fr && fr.height ? fr.top - hr.top - 24 : sh - 40;
        var dotY = bottom - labH - 14;
        var m = Math.max(40, sw * 0.055);
        var order = spots.slice().sort(function (a, b) { return parseFloat(a.style.left) - parseFloat(b.style.left); });
        var gap = order.length > 1 ? (sw - 2 * m - labW) / (order.length - 1) : 0;
        order.forEach(function (el, i) {
          var cx = order.length > 1 ? m + labW / 2 + i * gap : sw / 2;
          var dx = cx - parseFloat(el.style.left);
          var dy = dotY - parseFloat(el.style.top);
          el.__dx = dx;
          el.classList.remove('hh-spot--left', 'hh-spot--right');
          el.classList.add('hh-spot--below');
          el.style.setProperty('--dx', dx.toFixed(1) + 'px');
          el.style.setProperty('--dy', dy.toFixed(1) + 'px');
          el.style.setProperty('--len', Math.sqrt(dx * dx + dy * dy).toFixed(1) + 'px');
          el.style.setProperty('--ang', (Math.atan2(dy, dx) * 180 / Math.PI).toFixed(1) + 'deg');
        });
      }
    }

    /* Only touch a style when its value changes: every write makes the
       browser re-check the banner's styles. */
    var vars = {};
    var spotsHidden = false;
    function setVar(name, val) {
      if (vars[name] === val) return;
      vars[name] = val;
      style.setProperty(name, val);
    }

    function frame(force) {
      raf = 0;
      var total = track.offsetHeight - window.innerHeight;
      var r = track.getBoundingClientRect();
      var narrow = mqNarrow.matches;
      var raw = total > 0 ? clamp(-r.top / total, 0, 1) : 0;
      var p = narrow ? clamp(raw / PHONE_SPLIT, 0, 1) : raw;
      var q = narrow ? seg(raw, PHONE_SPLIT, 1) : 0;
      if (mqCalm.matches) { p = 0; q = 0; }
      if (!force && Math.abs(p - last) < 0.0004 && Math.abs(q - lastQ) < 0.0004) return;
      last = p;
      lastQ = q;

      /* Film. */
      var fpos = table(FILM, p);
      want = fpos;
      draw(force);

      /* Floors on the ceiling display. */
      var rise = seg(p, RISE[0], RISE[1]);
      var arrived = p >= RISE[1];
      var rising = rise > 0 && !arrived;
      var pos = rise * FLOORS.length;
      var idx = Math.min(FLOORS.length - 1, Math.floor(pos));
      var t = clamp(pos - idx, 0, 1);
      var label, lit;
      if (arrived) {
        label = 'R';
        lit = seg(p, RISE[1], RISE[1] + 0.015);
      } else if (!rising) {
        label = FLOORS[0];
        lit = 1;
      } else {
        label = FLOORS[idx];
        /* Blink once per floor: fade in, hold, fade out. Floor 1 is already
           showing when the ride starts, so it only fades out. */
        var fin = idx === 0 ? 1 : Math.min(1, t / BLINK);
        lit = Math.min(fin, Math.min(1, (1 - t) / BLINK));
      }
      if (screen) {
        if (label !== lastLabel) { screen.textContent = label; lastLabel = label; }
        screen.style.opacity = lit.toFixed(3);
      }
      hero.classList.toggle('is-rising', rising);

      /* The display sits in the lightbox and leaves with it. */
      var fs = Math.max(W / FW, H / FH);
      var oy = (H - FH * fs) / 2;
      var edge = table(LIGHTBOX, fpos);
      if (screen) {
        setVar('--hh-fy', (oy + edge * 0.5 * fs).toFixed(1) + 'px');
        setVar('--hh-fh', Math.max(0, edge * 0.62 * fs).toFixed(1) + 'px');
        setVar('--hh-fo', (1 - seg(fpos, 40, 45)).toFixed(3));
      }
      /* The soft blur over the lift walls on the opening screen clears as
         the ride starts (frames 0-12, while the lift is still). */
      setVar('--hh-veil', (1 - seg(p, 0.02, 0.10)).toFixed(3));
      hero.classList.toggle('is-veil', p < 0.10);

      /* Hand-over to the real photo, then the end-frame layer. */
      var fade = seg(p, 0.64, 0.68);
      var card = 1 - ease(seg(p, 0.36, 0.44));
      var copy = ease(seg(p, 0.42, 0.50));
      var panX = baseX;
      if (narrow) {
        panX = q < 0.33 ? lerp(0.5, 0.06, ease(seg(q, 0, 0.30))) : lerp(0.06, 0.84, ease(seg(q, 0.36, 0.94)));
      }

      setVar('--hh-card', card.toFixed(3));
      setVar('--hh-seq-o', (1 - fade).toFixed(3));
      setVar('--hh-end-s', '1');
      setVar('--hh-end-x', (panX * 100).toFixed(2) + '%');
      setVar('--hh-cta', seg(p, 0.48, 0.56).toFixed(3));
      setVar('--hh-copy', copy.toFixed(3));

      hero.classList.toggle('is-card-gone', card < 0.03);
      hero.classList.toggle('is-open', p >= 0.60);
      hero.classList.toggle('is-seq-gone', fade >= 1);
      var isEnd = p >= 0.54;
      if (isEnd !== hero.classList.contains('is-end')) {
        endLinks.forEach(function (a) { a.setAttribute('tabindex', isEnd ? '0' : '-1'); });
      }
      hero.classList.toggle('is-end', isEnd);
      hero.classList.toggle('is-copy', copy > 0.6);

      /* Markers only exist on the end frame; skip them during the film. */
      if (p < 0.60 && !force && spotsHidden) return;
      spotsHidden = p < 0.60;
      place(panX);
      var sp = seg(p, 0.68, 0.94);
      spots.forEach(function (el, i) {
        var o;
        if (narrow) {
          var d = Math.abs((el.__x || 0) - 0.5);
          o = p >= 0.68 ? clamp(1 - (d - 0.22) / 0.14, 0, 1) : 0;
        } else {
          o = seg(sp, i * 0.16, i * 0.16 + 0.36);
        }
        el.style.opacity = o.toFixed(3);
        el.style.scale = (0.85 + 0.15 * o).toFixed(3);
        el.classList.toggle('is-live', o > 0.6);
      });
    }

    /* Play button: scrolls through the whole story for the visitor, at an
       even pace (6s desktop, 9s phones, where the pan adds length). Any
       wheel, touch, key or click stops it and hands control back. */
    var playBtn = hero.querySelector('[data-hh-play]');
    var playing = false, playRaf = 0, stoppedAt = 0;
    var stopEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    var RAMP = 0.08, V = 1 / (1 - RAMP); /* speed-up and slow-down share, cruise speed */
    function stopPlay() {
      if (!playing) return;
      playing = false;
      stoppedAt = Date.now();
      cancelAnimationFrame(playRaf);
      hero.classList.remove('is-playing');
      stopEvents.forEach(function (ev) { window.removeEventListener(ev, stopPlay, true); });
    }
    function jump(y) {
      try { window.scrollTo({ top: y, behavior: 'instant' }); } catch (e) { window.scrollTo(0, y); }
    }
    if (playBtn) playBtn.addEventListener('click', function () {
      /* A click that just stopped the play (its mousedown) does not restart it. */
      if (playing || Date.now() - stoppedAt < 400) return;
      var top = track.getBoundingClientRect().top + window.pageYOffset;
      var end = top + track.offsetHeight - window.innerHeight;
      var from = window.pageYOffset;
      if (from >= end - 4) from = top;
      var dist = end - from;
      if (dist <= 0) return;
      var full = mqNarrow.matches ? 9000 : 6000;
      var dur = full * clamp(dist / Math.max(1, end - top), 0.3, 1.1);
      var t0 = 0;
      playing = true;
      hero.classList.add('is-playing');
      /* Added after this click has finished, so it does not stop itself. */
      setTimeout(function () {
        if (!playing) return;
        stopEvents.forEach(function (ev) { window.addEventListener(ev, stopPlay, { capture: true, passive: true }); });
      }, 0);
      var step = function (now) {
        if (!t0) t0 = now;
        var t = Math.min(1, (now - t0) / dur);
        /* Gentle start and finish, steady in between. */
        var e = t < RAMP ? V * t * t / (2 * RAMP) : (t > 1 - RAMP ? 1 - V * (1 - t) * (1 - t) / (2 * RAMP) : V * (t - RAMP / 2));
        jump(from + dist * e);
        frame(false);
        if (!playing) return;
        if (t < 1) { playRaf = requestAnimationFrame(step); } else { stopPlay(); }
      };
      playRaf = requestAnimationFrame(step);
    });

    /* Skip link (28 Sep): straight to the end of the story, then focus Shop. */
    /* Built here so the section file needs no change; a data-skip-label
       attribute on the section can rename it, "none" hides it. */
    var skipLabel = hero.getAttribute('data-skip-label') || 'Skip to shop';
    var skipBtn = hero.querySelector('[data-hh-skip]');
    if (!skipBtn && skipLabel !== 'none') {
      skipBtn = document.createElement('button');
      skipBtn.type = 'button';
      skipBtn.className = 'hh-skip';
      skipBtn.setAttribute('data-hh-skip', '');
      skipBtn.innerHTML = skipLabel + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6"/></svg>';
      hero.appendChild(skipBtn);
    }
    if (skipBtn) skipBtn.addEventListener('click', function () {
      stopPlay();
      var top = track.getBoundingClientRect().top + window.pageYOffset;
      jump(top + track.offsetHeight - window.innerHeight);
      frame(true);
      var shop = hero.querySelector('[data-hh-endlink]');
      if (shop) { try { shop.focus({ preventScroll: true }); } catch (e) { shop.focus(); } }
    });

    function kick() { if (!raf) raf = requestAnimationFrame(function () { frame(false); }); }
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', function () { measure(); centre(); offsets(); frame(true); });
    document.fonts && document.fonts.ready.then(function () { centre(); frame(true); });
    if (img && !img.complete) img.addEventListener('load', function () { frame(true); });
    frame(true);
  }

  /* Floor logos: a steady 40px a second, whatever the screen width. */
  function logoPace(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('.hh-floorlogos .t26-marquee__track'), function (track) {
      var set = track.querySelector('.hh-logoset');
      if (!set) return;
      function pace() {
        var w = set.offsetWidth;
        if (w) track.style.setProperty('--t26-marq-dur', Math.max(20, Math.round(w / 40)) + 's');
      }
      pace();
      window.addEventListener('resize', pace);
      window.addEventListener('load', pace);
    });
  }

  function boot(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('.hh-hero--glass'), init);
    logoPace(root);
  }
  boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
